import React, { useEffect, useState } from 'react';
import { theme } from '../../theme';
import { useResponsive } from '../../hooks/useResponsive';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  matchId: string;
  matchTitle?: string;
  // Match context for richer share messages (date/time/location + headcount).
  // Optional: if omitted the modal falls back to the generic message.
  matchDate?: string; // ISO date or "YYYY-MM-DD"
  matchTime?: string; // "HH:mm"
  matchLocation?: string;
  matchPlayersCount?: number;
  matchMaxPlayers?: number; // total spots (e.g. 14 for 7v7)
  onShowNotification: (message: string, type: 'success' | 'error' | 'warning' | 'info') => void;
}

// Construye un texto de share con contexto. Si no tenemos suficiente
// info (fecha/hora/lugar), caemos al mensaje genérico.
function buildShareMessage(opts: {
  shareUrl: string;
  matchTitle: string;
  matchDate?: string;
  matchTime?: string;
  matchLocation?: string;
  matchPlayersCount?: number;
  matchMaxPlayers?: number;
}): string {
  const { shareUrl, matchTitle, matchDate, matchTime, matchLocation, matchPlayersCount, matchMaxPlayers } = opts;

  // Sin contexto suficiente: fallback al mensaje legacy
  if (!matchDate || !matchTime || !matchLocation) {
    return `¡Hola! Te invito a unirte a este partido: ${matchTitle}\n\n${shareUrl}`;
  }

  // Día abreviado + DD/MM en español (Argentina). Si el parse falla, usamos el string raw.
  let datePart = matchDate;
  try {
    const d = new Date(`${matchDate}T${matchTime}`);
    if (!Number.isNaN(d.getTime())) {
      const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
      const dayName = dayNames[d.getDay()];
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      datePart = `${dayName} ${dd}/${mm}`;
    }
  } catch {
    // keep raw datePart
  }

  // Hora en formato HHhs (sin minutos si son 00, si no HH:MMhs)
  let timePart = matchTime;
  const timeMatch = matchTime.match(/^(\d{1,2}):(\d{2})/);
  if (timeMatch) {
    const hh = timeMatch[1];
    const mm = timeMatch[2];
    timePart = mm === '00' ? `${hh}hs` : `${hh}:${mm}hs`;
  }

  const headcount =
    typeof matchPlayersCount === 'number' && typeof matchMaxPlayers === 'number'
      ? ` Ya somos ${matchPlayersCount}/${matchMaxPlayers}.`
      : '';

  return `🟢 Partido ${datePart} a las ${timePart} en ${matchLocation}.${headcount} Anotate: ${shareUrl}`;
}

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  matchId,
  matchTitle = 'Partido',
  matchDate,
  matchTime,
  matchLocation,
  matchPlayersCount,
  matchMaxPlayers,
  onShowNotification
}) => {
  const { isMobile } = useResponsive();
  const [copied, setCopied] = useState(false);

  // Cache-buster para previews de WhatsApp/Telegram: refresca el token cada vez
  // que se abre el modal, así nuevas shares generan URLs distintas y los
  // crawlers no usan el preview viejo cacheado por URL.
  const [shareToken, setShareToken] = useState<number>(() => Date.now());
  useEffect(() => {
    if (isOpen) setShareToken(Date.now());
  }, [isOpen]);

  const baseClientUrl = process.env.REACT_APP_CLIENT_URL || window.location.origin;
  const shareUrl = `${baseClientUrl}/join/${matchId}?t=${shareToken}`;
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      onShowNotification('¡Link copiado al portapapeles!', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Fallback para navegadores que no soportan clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      setCopied(true);
      onShowNotification('¡Link copiado al portapapeles!', 'success');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareMessage = buildShareMessage({
    shareUrl,
    matchTitle,
    matchDate,
    matchTime,
    matchLocation,
    matchPlayersCount,
    matchMaxPlayers,
  });

  const handleShareWhatsApp = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleShareTelegram = () => {
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareMessage)}`;
    window.open(telegramUrl, '_blank');
  };

  const handleShareNative = async () => {
    if (navigator.share && typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: `Invitación a ${matchTitle}`,
          text: shareMessage,
          url: shareUrl
        });
      } catch (error) {
        // Usuario canceló o error
        // eslint-disable-next-line no-console
        console.log('Share cancelled or error:', error);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: isMobile ? '20px' : '40px'
    }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        padding: theme.spacing.lg,
        maxWidth: '500px',
        width: '100%',
        border: `2px solid ${theme.colors.primary}`
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing.lg,
          paddingBottom: theme.spacing.md,
          borderBottom: `1px solid ${theme.colors.border}`
        }}>
          <h2 style={{
            color: theme.colors.primary,
            fontFamily: theme.fonts.heading,
            fontSize: theme.fonts.sizeTitle,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.sm
          }}>
            <span style={{ fontSize: '24px' }}>📤</span>
            Compartir Partido
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: theme.colors.textSecondary,
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ marginBottom: theme.spacing.lg }}>
          <p style={{
            color: theme.colors.text,
            fontFamily: theme.fonts.body,
            fontSize: theme.fonts.sizeBody,
            margin: '0 0 16px 0'
          }}>
            Invita a otros jugadores a unirse a este partido:
          </p>
          
          <div style={{
            backgroundColor: theme.colors.background,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.input,
            padding: theme.spacing.sm,
            marginBottom: theme.spacing.md,
            wordBreak: 'break-word'
          }}>
            <div
              data-testid="share-modal-message-preview"
              style={{
                color: theme.colors.text,
                fontFamily: theme.fonts.body,
                fontSize: '13px',
                whiteSpace: 'pre-wrap',
              }}
            >
              {shareMessage}
            </div>
          </div>
        </div>

        {/* Share Options */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.sm
        }}>
          {/* Native Share (Mobile) */}
          {navigator.share && typeof navigator.share === 'function' && (
            <button
              onClick={handleShareNative}
              style={{
                padding: theme.spacing.sm,
                backgroundColor: theme.colors.primary,
                color: theme.colors.white,
                border: 'none',
                borderRadius: theme.borderRadius.button,
                fontFamily: theme.fonts.body,
                fontSize: theme.fonts.sizeBody,
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing.sm
              }}
            >
              <span>📱</span>
              <span>Compartir con Apps</span>
            </button>
          )}

          {/* WhatsApp */}
          <button
            onClick={handleShareWhatsApp}
            style={{
              padding: theme.spacing.sm,
              backgroundColor: '#25D366',
              color: theme.colors.white,
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontFamily: theme.fonts.body,
              fontSize: theme.fonts.sizeBody,
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.spacing.sm
            }}
          >
            <span>💬</span>
            <span>Compartir en WhatsApp</span>
          </button>

          {/* Telegram */}
          <button
            onClick={handleShareTelegram}
            style={{
              padding: theme.spacing.sm,
              backgroundColor: '#0088cc',
              color: theme.colors.white,
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontFamily: theme.fonts.body,
              fontSize: theme.fonts.sizeBody,
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.spacing.sm
            }}
          >
            <span>✈️</span>
            <span>Compartir en Telegram</span>
          </button>

          {/* Copy Link */}
          <button
            onClick={handleCopyLink}
            style={{
              padding: theme.spacing.sm,
              backgroundColor: copied ? theme.colors.success : theme.colors.textSecondary,
              color: theme.colors.white,
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontFamily: theme.fonts.body,
              fontSize: theme.fonts.sizeBody,
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.spacing.sm,
              transition: 'background-color 0.2s ease'
            }}
          >
            <span>{copied ? '✅' : '📋'}</span>
            <span>{copied ? '¡Copiado!' : 'Copiar Link'}</span>
          </button>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: theme.spacing.lg,
          paddingTop: theme.spacing.md,
          borderTop: `1px solid ${theme.colors.border}`,
          textAlign: 'center'
        }}>
          <p style={{
            color: theme.colors.textSecondary,
            fontFamily: theme.fonts.body,
            fontSize: theme.fonts.sizeSmall,
            margin: 0
          }}>
            El link permite a otros jugadores unirse al partido
          </p>
        </div>
      </div>
    </div>
  );
};
